import buildInfo from '@/build-info.json';
import { getColyseusEndpoint } from '@/config/online';
import { captureError } from '@/sentry';

export type VersionInfo = {
  hash: string;
  buildTime: string;
};

export function getClientVersion(): VersionInfo {
  return {
    hash: (buildInfo.hash || 'unknown').trim(),
    buildTime: buildInfo.buildTime || new Date().toISOString(),
  };
}

export function getVersionEndpoint(): string | null {
  const endpoint = getColyseusEndpoint();
  try {
    const url = new URL(endpoint);
    url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
    const basePath = url.pathname.replace(/\/$/, '');
    url.pathname = `${basePath || ''}/version-ui`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch (error) {
    captureError(error, { scope: 'version', action: 'getVersionEndpoint' });
    return null;
  }
}

let cachedServerVersion: VersionInfo | null | undefined = undefined;

export async function fetchServerVersion(): Promise<VersionInfo | null> {
  if (cachedServerVersion !== undefined) {
    return cachedServerVersion;
  }

  const url = getVersionEndpoint();
  if (!url) {
    cachedServerVersion = null;
    return null;
  }

  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      cachedServerVersion = null;
      return null;
    }

    const data = await response.json();
    if (!data || typeof data.hash !== 'string') {
      cachedServerVersion = null;
      return null;
    }

    cachedServerVersion = {
      hash: (data.hash || '').trim(),
      buildTime: typeof data.buildTime === 'string' ? data.buildTime : '',
    };
    return cachedServerVersion;
  } catch (error) {
    captureError(error, { scope: 'version', action: 'fetchServerVersion' });
    cachedServerVersion = null;
    return null;
  }
}

export function formatVersionText(client: VersionInfo, server?: VersionInfo | null): string {
  const hash = resolveHashDisplay(client.hash, server?.hash);
  const build = formatBuildTime(client.buildTime);
  return `Version: ${hash} • Build: ${build}`;
}

function resolveHashDisplay(clientHashRaw: string, serverHashRaw?: string): string {
  const clientHash = normalizeHash(clientHashRaw) || 'unknown';
  const serverHash = normalizeHash(serverHashRaw);

  if (!serverHash) return clientHash;
  if (serverHash === clientHash) return clientHash;
  return `${clientHash} / ${serverHash}`;
}

function formatBuildTime(buildTime: string): string {
  const date = new Date(buildTime);
  if (Number.isNaN(date.getTime())) {
    return buildTime || 'unknown';
  }

  return date.toLocaleString();
}

function normalizeHash(hash?: string): string {
  return (hash || '').trim();
}
